/**
 * Base API utilities shared across all API modules.
 */

// In production, React is served by Django from the same domain, so use relative URLs
// In development, use localhost:8000 (Django runserver default)
export const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

/**
 * Error carrying the HTTP status and parsed body so callers can branch on
 * structured fields (e.g. requires_verification, tier_limit) instead of
 * string-matching the message.
 */
export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;

  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Get CSRF token from cookie (set by Django)
 */
export function getCsrfToken(): string | null {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue;
    }
  }
  return null;
}

/**
 * Generic fetch wrapper with CSRF token handling
 *
 * @param endpoint - API endpoint path
 * @param options - Standard RequestInit options
 * @param timeoutMs - Optional timeout in milliseconds (default: no timeout)
 */
export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs?: number
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  // Add CSRF token for mutating requests
  const method = options.method?.toUpperCase() || 'GET';
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
  }

  // Set up abort controller for timeout
  let signal = options.signal;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (timeoutMs && !signal) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers,
      signal,
    });
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    throw error;
  }

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }

  if (response.status === 403) {
    // 403 bodies carry real user-facing reasons (unverified email, tier
    // limits) — surface them instead of a generic 'Unauthorized'.
    const body = await response.json().catch(() => ({} as Record<string, unknown>));
    const message = body.detail || body.message || body.error;
    if (typeof message === 'string' && message) {
      throw new ApiError(message, 403, body);
    }
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const detail = error.detail || error.message || error.error;
    // ApiError, not a plain Error: it carries the status and body, which is
    // the only way a caller can tell "the server explained itself" (a 409's
    // sentence is worth showing someone) from "the request never landed"
    // (whose message is transport noise). Both used to arrive identically.
    throw new ApiError(
      typeof detail === 'string' && detail ? detail : `API error: ${response.status}`,
      response.status,
      error,
    );
  }

  // Handle 204 No Content (e.g., DELETE responses, empty results)
  if (response.status === 204) {
    if (timeoutId) clearTimeout(timeoutId);
    return null as unknown as T;
  }

  // Guard against empty response bodies (e.g., server returning 200 with no content)
  const text = await response.text();
  if (timeoutId) clearTimeout(timeoutId);
  if (!text) {
    return null as unknown as T;
  }
  return JSON.parse(text);
}
